/*
 * Colour switching on product pages (one product per colour, see snippets/swatches.liquid).
 *
 * 1. Complete colour list: the swatches come from each product's custom.product_swatches list.
 *    In the background this asks search for every product titled "<model> - <colour>"
 *    (templates/search.colours.liquid) and adds any colour the list is missing, so a new colour
 *    product shows up on its sister products without editing them.
 *
 * 2. Instant switch: clicking a colour swaps the page sections (product, icons, related) for the
 *    other colour's, through the Section Rendering API, and updates the URL, title and canonical
 *    link. Each colour keeps its own real page and link, so search engines and shared links are
 *    unaffected. The theme's section scripts are unloaded and re-loaded through its registry
 *    (window.Shopify.theme.sections). If anything differs (another product template, an error),
 *    it falls back to a normal page load.
 *
 *    Vitals builds its reviews and "also bought" widgets once per page load and can't refresh
 *    them. A colour whose reviews differ from the ones on screen (not in the same Vitals review
 *    group) loads its page normally; otherwise the switch is instant and the reviews stay.
 *    "also bought" is hidden after a switch. The sticky Add to cart is the theme's own
 *    (snippets/product-sticky-atc.liquid) and follows the switch.
 *    ?instant_colours=0 turns the instant switch off for this browser session (normal page loads,
 *    preloaded on hover where supported); ?instant_colours=1 turns it back on.
 */
(function () {
  'use strict';

  if (window.colourSwitchLoaded) return;
  window.colourSwitchLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var FORCE_KEY = 'joiia-instant-colours';
  var SWATCH = '[data-colour-siblings] a.swatch';

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch (e) {
      // Private mode or storage full: nothing to remember.
    }
  }

  (function readForceParam() {
    var param = new URLSearchParams(window.location.search).get('instant_colours');
    if (param === '0') storageSet(FORCE_KEY, 'off');
    if (param === '1') storageSet(FORCE_KEY, null);
  })();

  function vitalsPresent() {
    return typeof window.vtlsLiquidData !== 'undefined' || document.getElementById('vtlsAebBundle') !== null;
  }

  function instantEnabled() {
    return storageGet(FORCE_KEY) !== 'off';
  }

  function swatchHandles(container) {
    return Array.prototype.map.call(container.querySelectorAll('a.swatch'), function (link) {
      return link.getAttribute('data-handle');
    });
  }

  function currentHandle() {
    var meta = document.querySelector('[data-colour-page-meta]');
    return meta ? meta.getAttribute('data-handle') : null;
  }

  function markCurrent(container) {
    var handle = currentHandle();
    container.querySelectorAll('a.swatch').forEach(function (link) {
      if (link.getAttribute('data-handle') === handle) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  /* ---------- 1. Complete colour list ---------- */

  function applySiblings(container, html) {
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-colour-siblings]');
    if (!fresh) return;
    var have = swatchHandles(container);
    var found = swatchHandles(fresh);
    var missing = found.filter(function (handle) {
      return have.indexOf(handle) === -1;
    });
    if (!missing.length) return;

    var keepsAll = have.every(function (handle) {
      return found.indexOf(handle) !== -1;
    });
    if (keepsAll && DEFAULT_LOCALE) {
      // Search knows every colour the list has, plus new ones: use its grouped, ordered list.
      // (On other languages only the new colours are added, so names keep their translation.)
      container.innerHTML = fresh.innerHTML;
    } else {
      // Keep the list as it is and add the new colours at the end.
      var target = container.querySelector('.colour-group:last-child .colour-group__swatches');
      if (!target) {
        var group = document.createElement('div');
        group.className = 'colour-group';
        target = document.createElement('div');
        target.className = 'colour-group__swatches';
        group.appendChild(target);
        container.appendChild(group);
      }
      missing.forEach(function (handle) {
        var link = fresh.querySelector('a.swatch[data-handle="' + handle + '"]');
        if (link) target.appendChild(document.importNode(link, true));
      });
    }
    markCurrent(container);
  }

  // Search only matches the store's main-language (English) titles, so on other languages the
  // model name comes from the English product data and links are pointed back at this language.
  var DEFAULT_LOCALE = ROOT === '/';

  function englishModelTitle(container) {
    if (DEFAULT_LOCALE) return Promise.resolve(container.getAttribute('data-model-title'));
    var handle = currentHandle();
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + handle + '.js', {credentials: 'same-origin'})
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (product) {
        var parts = product ? product.title.split(' - ') : [];
        return parts.length === 2 ? parts[0].trim() : null;
      });
  }

  function localiseLinks(html) {
    if (DEFAULT_LOCALE) return html;
    return html.replace(/href="\/products\//g, 'href="' + ROOT + 'products/');
  }

  function completeSiblings() {
    var container = document.querySelector('[data-colour-siblings]');
    if (!container || !container.getAttribute('data-model-title')) return;

    englishModelTitle(container)
      .then(function (model) {
        if (!model) return;
        var key = 'joiia-colours-v2:' + model;
        var cached = storageGet(key);
        if (cached) return cached;

        var url = '/search?q=' + encodeURIComponent('title:"' + model + '"') + '&type=product&view=colours&options%5Bprefix%5D=none';
        return fetch(url, {credentials: 'same-origin'})
          .then(function (response) {
            return response.ok ? response.text() : '';
          })
          .then(function (html) {
            if (html) storageSet(key, html);
            return html;
          });
      })
      .then(function (html) {
        if (html) applySiblings(container, localiseLinks(html));
      })
      .catch(function () {
        // The list from the product stays as it is.
      });
  }

  /* ---------- 2. Instant switch ---------- */

  var sectionCache = {};
  var switched = false;

  function sectionIds() {
    var main = document.getElementById('MainContent');
    if (!main) return [];
    return Array.prototype.filter
      .call(main.children, function (element) {
        return element.classList.contains('shopify-section') && element.id.indexOf('shopify-section-template--') === 0;
      })
      .map(function (element) {
        return element.id.replace('shopify-section-', '');
      })
      .slice(0, 5);
  }

  function fetchSections(url) {
    if (!sectionCache[url]) {
      var ids = sectionIds();
      var requestUrl = new URL(url, window.location.href);
      requestUrl.searchParams.set('sections', ids.join(','));
      sectionCache[url] = fetch(requestUrl.toString(), {credentials: 'same-origin'})
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (json) {
          return {ids: ids, json: json};
        })
        .catch(function (error) {
          delete sectionCache[url];
          throw error;
        });
    }
    return sectionCache[url];
  }

  function updatePageMeta(url) {
    var meta = document.querySelector('[data-colour-page-meta]');
    if (!meta) return;
    var title = meta.getAttribute('data-title');
    var description = meta.getAttribute('data-description');
    var canonical = meta.getAttribute('data-canonical');
    if (title) document.title = title;
    var setAttr = function (selector, attr, value) {
      var element = document.querySelector(selector);
      if (element && value) element.setAttribute(attr, value);
    };
    setAttr('meta[name="description"]', 'content', description);
    setAttr('link[rel="canonical"]', 'href', canonical);
    setAttr('meta[property="og:url"]', 'content', canonical || url);
    setAttr('meta[property="og:title"]', 'content', title);
  }

  // A colour switch should feel like the same page: swapped content appears in place, without the
  // theme's fade/slide-in animations. Removing data-aos turns them off for that content. Sections
  // that fill in content after loading (e.g. "You may also like" fetches its products) are covered
  // too; the theme's scroll animations only watch what was there at page load anyway.
  var watchingLateContent = false;
  function showLateContent() {
    var main = document.getElementById('MainContent');
    if (watchingLateContent || !main || !window.MutationObserver) return;
    watchingLateContent = true;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          stillContent(node);
        });
      });
    }).observe(main, {childList: true, subtree: true});
  }

  function stillContent(root) {
    if (root.nodeType !== 1) return;
    if (root.hasAttribute('data-aos')) root.removeAttribute('data-aos');
    root.querySelectorAll('[data-aos]').forEach(function (element) {
      element.removeAttribute('data-aos');
    });
  }

  // Inline scripts in the swapped sections (e.g. "View all colours" and the size guide set
  // themselves up this way) don't run when HTML is inserted, so they are re-run. Their pop-ups move
  // themselves to the end of the page, so the previous colour's pop-ups are removed first.
  function removeOldPopups() {
    document.querySelectorAll('body > [data-sg-overlay]').forEach(function (overlay) {
      overlay.remove();
    });
  }

  function runInlineScripts(root) {
    root.querySelectorAll('script').forEach(function (old) {
      var type = (old.getAttribute('type') || '').toLowerCase();
      if (old.src || (type && type !== 'text/javascript' && type !== 'module')) return;
      var script = document.createElement('script');
      if (type) script.type = type;
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  }

  /* Vitals widgets after a switch. "Customers also bought" is for one product, so it is hidden.
     Reviews: Vitals shows the page's original product's reviews, or its review group's (a group
     pools the reviews of several colours). Before switching, the next colour's review data (same
     public URL Vitals used, with the new product ID) is compared with what is shown:
     - same reviews (same group): instant switch, the review block stays;
     - different reviews: normal page load, so the right reviews show;
     - Vitals hasn't loaded reviews yet: instant switch, then checked as soon as it loads them,
       and the block is hidden if it doesn't match the colour on screen. */
  var REVIEW_DATA = /\/sf\/pr\/(grp|prd)\/\d+\/p1\/\d+\.json/;
  var vitalsStyle = null;
  function vitalsCss(hideReviews) {
    if (!vitalsPresent()) return;
    if (!vitalsStyle) {
      vitalsStyle = document.createElement('style');
      vitalsStyle.id = 'colour-switch-vitals';
      document.head.appendChild(vitalsStyle);
    }
    vitalsStyle.textContent =
      '#bundle-sticky_add_to_cart, .vtl-rp-main-widget' + (hideReviews ? ', .vtl-pr-main-widget' : '') + ' { display: none !important; }';
  }

  function loadedReviewUrl() {
    return (
      performance
        .getEntriesByType('resource')
        .map(function (entry) {
          return entry.name;
        })
        .filter(function (name) {
          return REVIEW_DATA.test(name);
        })[0] || null
    );
  }

  var reviewSets = {};
  function reviewSet(url) {
    if (!reviewSets[url]) {
      reviewSets[url] = fetch(url)
        .then(function (response) {
          if (!response.ok) return 'none';
          return response.json().then(function (data) {
            var ids = (data.reviews || []).map(function (review) {
              return review.id;
            });
            return (data.reviewStats ? data.reviewStats.r : 0) + ':' + ids.sort().join(',');
          });
        })
        .catch(function () {
          return 'error';
        });
    }
    return reviewSets[url];
  }

  // true: the colour has the reviews on screen; false: different reviews; null: unknown yet.
  function sameReviews(productId) {
    var loaded = loadedReviewUrl();
    if (!loaded || !productId || !document.querySelector('.vtl-pr-main-widget')) return Promise.resolve(null);
    var next = loaded.replace(/\/p1\/\d+\.json.*$/, '/p1/' + productId + '.json');
    if (next === loaded) return Promise.resolve(true);
    return Promise.all([reviewSet(loaded), reviewSet(next)]).then(function (sets) {
      return sets[0] === sets[1];
    });
  }

  // The product ID from the other colour's fetched page sections (page meta in the main section).
  function productIdFrom(result) {
    var found = null;
    Object.keys(result.json || {}).some(function (id) {
      var match = /data-colour-page-meta[^>]*data-product-id="(\d+)"/.exec(result.json[id] || '');
      if (match) found = match[1];
      return !!found;
    });
    return found;
  }

  function currentProductId() {
    var meta = document.querySelector('[data-colour-page-meta]');
    return meta ? meta.getAttribute('data-product-id') : null;
  }

  function checkShownReviews() {
    sameReviews(currentProductId()).then(function (same) {
      vitalsCss(same === false);
    });
  }

  var watchingReviews = false;
  function updateVitalsReviews() {
    vitalsCss(false);
    if (loadedReviewUrl()) {
      checkShownReviews();
      return;
    }
    // Vitals loads later (delayed script): check once it fetches its review data.
    if (watchingReviews || !window.PerformanceObserver) return;
    watchingReviews = true;
    try {
      new PerformanceObserver(function (list) {
        var found = list.getEntries().some(function (entry) {
          return REVIEW_DATA.test(entry.name);
        });
        if (found) window.setTimeout(checkShownReviews, 300);
      }).observe({type: 'resource'});
    } catch (e) {
      // Older browsers: the block stays as Vitals renders it.
    }
  }

  function swapTo(url, push) {
    return fetchSections(url).then(function (result) {
      var registry = window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
      var json = result.json;
      if (!result.ids.length || result.ids.some(function (id) { return !json[id]; })) {
        throw new Error('Different page template');
      }
      if (!registry || !registry.instances || !registry.instances.length) throw new Error('No section registry');
      var SectionClass = registry.instances[0].constructor;
      showLateContent();
      removeOldPopups();

      result.ids.forEach(function (id) {
        var oldWrapper = document.getElementById('shopify-section-' + id);
        var holder = document.createElement('div');
        holder.innerHTML = json[id];
        var newWrapper = holder.querySelector('#shopify-section-' + id) || holder.firstElementChild;
        if (!oldWrapper || !newWrapper) return;

        for (var i = registry.instances.length - 1; i >= 0; i--) {
          var instance = registry.instances[i];
          if (oldWrapper.contains(instance.container)) {
            try {
              instance.onUnload();
            } catch (e) {
              // A section that fails to unload is replaced anyway.
            }
            registry.instances.splice(i, 1);
          }
        }

        stillContent(newWrapper);
        oldWrapper.replaceWith(newWrapper);
        runInlineScripts(newWrapper);

        newWrapper.querySelectorAll('[data-section-type]').forEach(function (container) {
          var registration = registry.registered[container.getAttribute('data-section-type')];
          if (registration) registry.instances.push(new SectionClass(container, registration));
        });
      });

      if (push) {
        if (!switched) window.history.replaceState({colourSwitch: true}, '', window.location.href);
        window.history.pushState({colourSwitch: true}, '', url);
      }
      switched = true;
      document.documentElement.classList.add('colour-switched');
      updateVitalsReviews();
      updatePageMeta(url);
      completeSiblings();

      var current = document.querySelector(SWATCH + '[aria-current="true"]');
      if (current) current.focus({preventScroll: true});

      document.dispatchEvent(new CustomEvent('joiia:colour:switched', {bubbles: true, detail: {url: url}}));
    });
  }

  function swatchFromEvent(event) {
    return event.target.closest ? event.target.closest(SWATCH) : null;
  }

  document.addEventListener('click', function (event) {
    var link = swatchFromEvent(event);
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!instantEnabled()) return;

    event.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;

    var url = link.href;
    document.documentElement.classList.add('is-colour-switching');
    fetchSections(url)
      .then(function (result) {
        return sameReviews(link.getAttribute('data-product-id') || productIdFrom(result));
      })
      .then(function (same) {
        // This colour has other reviews than the ones on screen: load its page normally.
        if (same === false) {
          window.location.href = url;
          return new Promise(function () {});
        }
        return swapTo(url, true);
      })
      .catch(function () {
        window.location.href = url;
      })
      .then(function () {
        document.documentElement.classList.remove('is-colour-switching');
      });
  });

  // Fetch the other colour as soon as the pointer or finger reaches it, so the click is instant.
  function prefetch(event) {
    var link = swatchFromEvent(event);
    if (!link || !instantEnabled() || link.getAttribute('aria-current') === 'true') return;
    fetchSections(link.href).catch(function () {});
    sameReviews(link.getAttribute('data-product-id'));
  }
  document.addEventListener('mouseover', prefetch, {passive: true});
  document.addEventListener('touchstart', prefetch, {passive: true});

  window.addEventListener('popstate', function () {
    if (!switched) return;
    swapTo(window.location.href, false).catch(function () {
      window.location.reload();
    });
  });

  // While Vitals keeps colour clicks as page loads, let supporting browsers preload the page on hover.
  if (!instantEnabled() && window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
    var rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({prefetch: [{source: 'document', where: {selector_matches: SWATCH}, eagerness: 'moderate'}]});
    document.head.appendChild(rules);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', completeSiblings);
  } else {
    completeSiblings();
  }
})();
